import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import PersonCell from '../../../components/ui/PersonCell';

// isActive=false satırları yalnız includeInactive[eq]=true ile listeye giriyor (bkz.
// hasMissingEmploymentDocs/includeInactive filtre kolonları aşağıda) — bu rozet o durumda
// kullanıcının aktif/çıkmış ayrımını satırdan görebilmesini sağlıyor.
const InactiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ theme }) => `${theme.colors.auxiliar.danger}1F`};
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

// `eq]=true` dışında anlamlı bir değeri olmayan tek yönlü filtreler (bkz. backend sözleşmesi):
// hasMissingEmploymentDocs ve includeInactive gerçek bir satır alanı DEĞİL, sorgu bayrağıdır —
// bu yüzden 'employees' sayfasındaki canSeeAll=false dalındaki gizli firstName/lastName
// kolonları gibi display:'excluded' bir filtre-kolonu olarak modelleniyor.
const booleanFlagFilterColumn = (name, label, yesLabel) => ({
  name,
  label,
  options: {
    filter: true,
    sort: false,
    display: 'excluded',
    filterType: 'custom',
    filterOptions: {
      defaultOp: 'eq',
      display: (filterList, onChange, index, column) => (
        <FormControl variant="standard" fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            value={filterList[index][0] || ''}
            onChange={(e) => {
              filterList[index] = e.target.value ? [e.target.value] : [];
              onChange(filterList[index], index, column);
            }}
          >
            <MenuItem value=""><em>-</em></MenuItem>
            <MenuItem value="true">{yesLabel}</MenuItem>
          </Select>
        </FormControl>
      ),
    },
  },
});

export default function usePersonnelFilesApi(queryParams) {
  const { t } = useTranslation();
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 25, pageIndex: 0 });
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const merged = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : (lastValidQueryParams.current || { pageNumber: 1, pageSize: 25 });
    lastValidQueryParams.current = merged;
    setFetching(true);
    api.get(`/v1/personnel-files?${new URLSearchParams(merged)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 25,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams]);

  const columns = useMemo(() => [
    { name: 'employeeId', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'fullName',
      label: t('personnelFile.list.employee'),
      options: {
        customBodyRender: (v, tableMeta) => (
          <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.profilePicture} />
        ), filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } },
    },
    {
      name: 'departmentName',
      label: t('personnelFile.list.department'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      // Yalnızca çıkmış (isActive=false) satırlarda görünür — includeInactive[eq]=true
      // filtresi açık olmadan bu satırlar zaten listeye girmiyor.
      name: 'isActive',
      label: t('personnelFile.list.status'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => (v ? null : <InactiveBadge>{t('personnelFile.list.formerBadge')}</InactiveBadge>),
      },
    },
    // Aşağıdaki dört sayaç yalnızca DEVAM EDEN istihdam belge gereksinimlerini sayar — Task 9'un
    // PersonnelFileView'da gösterdiği ATS/preboarding/offboarding dahil BİRLEŞİK dosyanın
    // tamamını DEĞİL. Başlıklar bunu her sütunda açıkça "İstihdam Belgeleri" diyerek belirtir;
    // aksi halde kullanıcı bu sayıları tüm özlük dosyasının özeti sanabilir.
    {
      name: 'employmentTotalCount',
      label: t('personnelFile.list.employmentTotal'),
      options: { filter: false, sort: false },
    },
    {
      name: 'employmentPendingCount',
      label: t('personnelFile.list.employmentPending'),
      options: { filter: false, sort: false },
    },
    {
      name: 'employmentAwaitingApprovalCount',
      label: t('personnelFile.list.employmentAwaiting'),
      options: { filter: false, sort: false },
    },
    {
      name: 'employmentExpiredCount',
      label: t('personnelFile.list.employmentExpired'),
      options: { filter: false, sort: false },
    },
    booleanFlagFilterColumn('hasMissingEmploymentDocs', t('personnelFile.list.missingFilter'), t('common.yes')),
    booleanFlagFilterColumn('includeInactive', t('personnelFile.list.includeInactiveFilter'), t('common.yes')),
  ], [t, data]);

  return { fetching, data, pagination, columns };
}
